import { ReactElement } from "react";
import styled from "styled-components";
import { RichContentBlock, RichContentData } from "../../../types/richContent";
import { getBlockComponent } from "./registry";

/**
 * Renders authored content (the API's reconstructed {blocks:[{type,data}]}) straight
 * through the design system — replaces the old EditorJsReader, which booted the whole
 * EditorJS engine in readOnly mode with its own desktop CSS and ignored the study theme.
 *
 * Accepts either a full RichContentData or a plain {blocks} slice, so callers that
 * page content themselves (SwiperOnboarding splits on delimiter blocks) hand each
 * page straight in.
 */
interface ContentRendererProps {
  data?: RichContentData | { blocks: RichContentBlock[] } | null;
}

// Base typography + vertical rhythm for a content page; each block supplies its own
// internals. Colors/fonts ride the per-study theme tokens (theme/tokens.ts).
// The reading column caps at 720px (centered): on phones that's a no-op, on desktops
// it keeps images/videos viewable (a 1200px-wide 16:10 hero is taller than the
// viewport) and line lengths readable — and it matches the Diapason editor canvas,
// so authors compose in the same column participants read.
const Flow = styled.div`
  max-width: 720px;
  margin-inline: auto;
  font-family: var(--app-font-family, "Roboto", sans-serif);
  color: var(--app-color-text, #041723);
  font-size: 16px;
  line-height: 1.6;

  > * + * {
    margin-top: 14px;
  }
`;

const ContentRenderer = ({ data }: ContentRendererProps): ReactElement => (
  <Flow>
    {(data?.blocks ?? []).map((block, index) => {
      const Block = getBlockComponent(block?.type);
      if (!Block) return null; // unknown/future type — skip, never crash
      return <Block key={block.id ?? index} data={block.data ?? {}} />;
    })}
  </Flow>
);

export default ContentRenderer;
